import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { useBrand } from "@/config/brands";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Scale,
  Shield,
  Gavel,
  Trophy,
  DollarSign,
  AlertTriangle,
  FileText,
  ExternalLink,
  Mail,
} from "lucide-react";

// ─── Types ───────────────────────────────────────────────────────────────────

type LawsuitOutcome =
  | "pending"
  | "dismissed"
  | "dropped"
  | "settled"
  | "won"
  | "lost";

interface Lawsuit {
  id: string;
  hospitalName: string;
  healthSystem?: string;
  dateFiled: string;
  court: string;
  jurisdiction: string;
  allegedViolation: string;
  theirEstLegalCosts: number | null;
  ourLegalCosts: number;
  outcome: LawsuitOutcome;
  notes?: string;
}

// ─── Data (starts empty — each entry is proof of their desperation) ──────────
const LAWSUITS: Lawsuit[] = [];

// ─── Helpers ─────────────────────────────────────────────────────────────────

const fmt = (n: number) =>
  n === 0 ? "$0" : `$${n.toLocaleString()}`;

const outcomeBadge = (outcome: LawsuitOutcome) => {
  const map: Record<LawsuitOutcome, { label: string; color: string }> = {
    pending:   { label: "Pending",          color: "#C5A55A" },
    dismissed: { label: "Dismissed",        color: "#2b5e49" },
    dropped:   { label: "Dropped",          color: "#2b5e49" },
    settled:   { label: "Settled",          color: "#6b7280" },
    won:       { label: "We Won",           color: "#2b5e49" },
    lost:      { label: "Lost",             color: "#B22234" },
  };
  const { label, color } = map[outcome];
  return (
    <Badge style={{ backgroundColor: color, color: "#fff", border: "none" }}>
      {label}
    </Badge>
  );
};

const totalDamagesPaid = LAWSUITS.reduce((sum, l) => sum + l.ourLegalCosts, 0);
const totalTheirCosts = LAWSUITS.reduce(
  (sum, l) => sum + (l.theirEstLegalCosts ?? 0),
  0
);
const dismissed = LAWSUITS.filter(
  (l) => l.outcome === "dismissed" || l.outcome === "dropped" || l.outcome === "won"
).length;

// ─── Component ───────────────────────────────────────────────────────────────

const LawsuitsAgainstUs = () => {
  const brand = useBrand();
  const isAhp = brand.key === "ahp";

  // ── AHP palette ──────────────────────────────────────────────────────────
  const cream  = "#FAF9F6";
  const green  = "#2b5e49";
  const navy   = "#002868";
  const gold   = "#C5A55A";
  const red    = "#B22234";
  const ink    = "#1B1B1B";
  const muted  = "#6b7280";
  const border = "#e8d4b0";

  const serif = "'Instrument Serif', Georgia, serif";
  const sans  = "'Instrument Sans', system-ui, sans-serif";

  // ── Luigi's palette ───────────────────────────────────────────────────────
  // handled via Tailwind classes below

  // ─── Stat cards ──────────────────────────────────────────────────────────
  const stats = [
    {
      icon: <Gavel className="w-6 h-6" />,
      label: "Hospitals That Sued Us",
      value: LAWSUITS.length,
      display: LAWSUITS.length.toString(),
      color: isAhp ? red : undefined,
      note: "so far",
    },
    {
      icon: <DollarSign className="w-6 h-6" />,
      label: "Total We've Paid in Damages",
      value: totalDamagesPaid,
      display: fmt(totalDamagesPaid),
      color: isAhp ? green : undefined,
      note: "ever",
    },
    {
      icon: <Scale className="w-6 h-6" />,
      label: "Their Estimated Legal Costs",
      value: totalTheirCosts,
      display: fmt(totalTheirCosts),
      color: isAhp ? gold : undefined,
      note: "money they spent to silence us",
    },
    {
      icon: <Trophy className="w-6 h-6" />,
      label: "Cases Dismissed or Dropped",
      value: dismissed,
      display: dismissed.toString(),
      color: isAhp ? navy : undefined,
      note: "truth is a complete defense",
    },
  ];

  const reasonCards = [
    {
      icon: <Shield className="w-5 h-5" />,
      title: "Judgment-Proof",
      body:
        "After spending millions on medical care, there are no assets to seize. A lawsuit costs them $50,000–$200,000 in legal fees. It costs us $0. The financial math does not favor them.",
    },
    {
      icon: <Scale className="w-5 h-5" />,
      title: "Pro Se Representation",
      body:
        "We represent ourselves. Constitutional right, zero attorney cost. We have time, facts, and the law on our side. They have billing partners.",
    },
    {
      icon: <AlertTriangle className="w-5 h-5" />,
      title: "The Streisand Effect",
      body:
        "Every lawsuit becomes national news. A billion-dollar health system suing a website called 'Victims of Late Stage Capitalism' for reporting their prices is the story. Their PR team knows this.",
    },
    {
      icon: <FileText className="w-5 h-5" />,
      title: "Public Evidence, Forever",
      body:
        "Court filings are public record. Every complaint they file becomes permanent, searchable documentation of which hospitals tried to silence price transparency. That document lives longer than the case.",
    },
    {
      icon: <Gavel className="w-5 h-5" />,
      title: "Anti-SLAPP Laws",
      body:
        "~32 states have anti-SLAPP statutes protecting public interest reporting. Strategic litigation against public participation can result in the plaintiff paying our legal fees — even if we represent ourselves.",
    },
    {
      icon: <Trophy className="w-5 h-5" />,
      title: "The Brand Is the Defense",
      body:
        "Our operating entity is Victims of Late Stage Capitalism. Each lawsuit is a press release. Each dismissal is a donation campaign. Every threat letter gets framed and posted here.",
    },
  ];

  const today = new Date().toISOString().split('T')[0];

  const seoTitle = isAhp
    ? `Hospitals That Sued Us For Telling The Truth | ${brand.name}`
    : `Lawsuits Against Us | ${brand.name}`;

  const seoDescription = isAhp
    ? `A permanent public record of every hospital that sued ${brand.name} for publishing federally-required price transparency data under 45 CFR Part 180. Every case documented. Every dismissal recorded. Total damages paid by us: $0.`
    : `Every hospital that sued Luigi's Hall of Shame for exposing healthcare price gouging — documented publicly and permanently. Truth is a complete defense.`;

  const canonicalUrl = `${brand.primaryDomain}/lawsuits`;

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": seoTitle,
    "description": seoDescription,
    "url": canonicalUrl,
    "dateModified": today,
    "publisher": {
      "@type": "Organization",
      "name": brand.name,
      "url": brand.primaryDomain
    },
    "about": {
      "@type": "Thing",
      "name": "Hospital Price Transparency Enforcement",
      "description": "Legal proceedings related to the CMS Hospital Price Transparency Rule (45 CFR Part 180)"
    },
    "breadcrumb": {
      "@type": "BreadcrumbList",
      "itemListElement": [
        { "@type": "ListItem", "position": 1, "name": "Home", "item": brand.primaryDomain },
        { "@type": "ListItem", "position": 2, "name": "Lawsuits Against Us", "item": canonicalUrl }
      ]
    }
  };

  const helmetBlock = (
    <Helmet>
      <title>{seoTitle}</title>
      <meta name="description" content={seoDescription} />
      <meta name="keywords" content="hospital price transparency lawsuit, 45 CFR Part 180, First Amendment healthcare, hospital SLAPP suit, price transparency enforcement, chargemaster lawsuit, healthcare watchdog" />
      <meta name="robots" content="index, follow" />
      <link rel="canonical" href={canonicalUrl} />
      {/* Open Graph */}
      <meta property="og:title" content={seoTitle} />
      <meta property="og:description" content={seoDescription} />
      <meta property="og:image" content={brand.social.ogImage} />
      <meta property="og:type" content="article" />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:site_name" content={brand.name} />
      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={seoTitle} />
      <meta name="twitter:description" content={seoDescription} />
      <meta name="twitter:image" content={brand.social.ogImage} />
      {brand.social.twitter && <meta name="twitter:site" content={brand.social.twitter} />}
      {/* JSON-LD */}
      <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
    </Helmet>
  );

  if (!isAhp) {
    // ── Luigi's Hall of Shame styling ─────────────────────────────────────
    return (
      <div className="min-h-screen bg-background">
        {helmetBlock}
        {/* Header */}
        <header className="border-b border-border bg-secondary/30">
          <div className="mx-auto px-6 py-12 max-w-6xl">
            <div className="flex items-center gap-3 mb-4">
              <Gavel className="w-10 h-10 text-terminal-green" />
              <h1 className="text-5xl font-bold text-terminal-green glitch font-mono" data-text="LAWSUITS AGAINST US">
                LAWSUITS AGAINST US
              </h1>
            </div>
            <p className="text-muted-foreground text-lg max-w-3xl font-mono">
              Every hospital that sues us for reporting their prices proves our point.
              We document every threat, every filing, every dismissal. Publicly. Forever.
            </p>
          </div>
        </header>

        <main className="mx-auto px-6 py-12 max-w-6xl space-y-12">
          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {stats.map((s) => (
              <Card key={s.label} className="terminal-card p-6 text-center">
                <div className="text-terminal-green mb-2 flex justify-center">{s.icon}</div>
                <div className="text-4xl font-mono font-bold text-terminal-green mb-1">{s.display}</div>
                <div className="text-xs text-muted-foreground uppercase tracking-wider mb-1">{s.label}</div>
                <div className="text-xs text-muted-foreground italic">{s.note}</div>
              </Card>
            ))}
          </div>

          {/* Why We Welcome */}
          <div>
            <h2 className="text-2xl font-mono font-bold text-terminal-green mb-6">
              // WHY WE WELCOME EVERY LAWSUIT
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {reasonCards.map((r) => (
                <Card key={r.title} className="terminal-card p-5">
                  <div className="flex items-center gap-2 mb-3 text-terminal-green">
                    {r.icon}
                    <span className="font-mono font-bold text-sm uppercase tracking-wider">{r.title}</span>
                  </div>
                  <p className="text-sm text-muted-foreground leading-relaxed">{r.body}</p>
                </Card>
              ))}
            </div>
          </div>

          {/* Table */}
          <div>
            <h2 className="text-2xl font-mono font-bold text-terminal-green mb-6">
              // CASE DOCKET
            </h2>
            <Card className="terminal-card p-0 overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="border-border">
                    <TableHead className="font-mono text-terminal-green">Hospital / System</TableHead>
                    <TableHead className="font-mono text-terminal-green">Date Filed</TableHead>
                    <TableHead className="font-mono text-terminal-green">Court</TableHead>
                    <TableHead className="font-mono text-terminal-green">Alleged Violation</TableHead>
                    <TableHead className="text-right font-mono text-terminal-green">Their Costs (Est.)</TableHead>
                    <TableHead className="text-right font-mono text-terminal-green">Our Costs</TableHead>
                    <TableHead className="text-center font-mono text-terminal-green">Outcome</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {LAWSUITS.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-16">
                        <div className="text-muted-foreground font-mono">
                          <div className="text-xl mb-2 text-terminal-green">No lawsuits filed against us. Yet.</div>
                          <div className="text-sm">Hospitals: our contact info is below.</div>
                          <div className="text-xs mt-4 opacity-50">This page will be updated in real time when that changes.</div>
                        </div>
                      </TableCell>
                    </TableRow>
                  ) : (
                    LAWSUITS.map((l) => (
                      <TableRow key={l.id} className="border-border/50">
                        <TableCell className="font-medium">
                          <div>{l.hospitalName}</div>
                          {l.healthSystem && <div className="text-xs text-muted-foreground">{l.healthSystem}</div>}
                        </TableCell>
                        <TableCell className="font-mono text-sm">{l.dateFiled}</TableCell>
                        <TableCell className="text-sm">{l.court}<div className="text-xs text-muted-foreground">{l.jurisdiction}</div></TableCell>
                        <TableCell className="text-sm max-w-[200px]">{l.allegedViolation}</TableCell>
                        <TableCell className="text-right font-mono text-sm">{l.theirEstLegalCosts ? fmt(l.theirEstLegalCosts) : "Unknown"}</TableCell>
                        <TableCell className="text-right font-mono text-sm text-terminal-green">{fmt(l.ourLegalCosts)}</TableCell>
                        <TableCell className="text-center">{outcomeBadge(l.outcome)}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </Card>
          </div>

          {/* Notice to hospitals */}
          <Card className="terminal-card p-8 border-yellow-500/30">
            <h2 className="text-xl font-mono font-bold text-yellow-500 mb-4">
              // IF YOU'RE A HOSPITAL CONSIDERING SUING US
            </h2>
            <div className="space-y-3 text-sm text-muted-foreground font-mono leading-relaxed max-w-3xl">
              <p>Everything we publish is factually accurate and derived from your own legally-mandated public disclosures.</p>
              <p>The First Amendment protects reporting on federal regulatory compliance. Truth is an absolute defense to defamation claims. Anti-SLAPP statutes in ~32 states allow us to recover costs when strategic litigation fails.</p>
              <p>We have no assets to seize. We represent ourselves. Your legal fees start at $50,000. Ours start at $0.</p>
              <p className="text-terminal-green font-bold">There is a faster, cheaper, and better option: update your chargemaster to comply with 45 CFR Part 180. We will mark you compliant within 24 hours of verification.</p>
              <div className="flex items-center gap-2 mt-4 pt-4 border-t border-border">
                <Mail className="w-4 h-4 text-terminal-green" />
                <span className="text-terminal-green">{brand.contact.support}</span>
              </div>
            </div>
          </Card>

          <p className="text-xs text-muted-foreground font-mono text-center">
            This platform is operated by Victims of Late Stage Capitalism. Each lawsuit filed against us is documented here as public record.
          </p>
        </main>
      </div>
    );
  }

  // ── AHP (American Hospital Prices) styling ────────────────────────────────
  return (
    <div className="min-h-screen" style={{ backgroundColor: cream, fontFamily: sans }}>
      {helmetBlock}

      {/* ── Hero ──────────────────────────────────────────────────────────── */}
      <header
        className="relative border-b overflow-hidden"
        style={{ borderColor: border, background: `linear-gradient(160deg, ${navy} 0%, #001540 100%)` }}
      >
        {/* Stars texture */}
        <div
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage:
              "radial-gradient(circle, #fff 1px, transparent 1px)",
            backgroundSize: "30px 30px",
          }}
        />

        <div className="relative mx-auto px-6 py-16 max-w-5xl">
          <div className="flex items-center gap-3 mb-6">
            <div
              className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0"
              style={{ backgroundColor: red }}
            >
              <Gavel className="w-6 h-6 text-white" />
            </div>
            <Badge
              className="text-xs font-semibold tracking-widest uppercase px-3 py-1"
              style={{ backgroundColor: `${red}33`, color: "#fff", border: `1px solid ${red}`, fontFamily: sans }}
            >
              Legal Record
            </Badge>
          </div>

          <h1
            className="text-5xl md:text-6xl font-bold leading-tight mb-4"
            style={{ color: "#fff", fontFamily: serif }}
          >
            Hospitals That Sued Us<br />
            <span style={{ color: gold }}>For Telling The Truth</span>
          </h1>

          <p
            className="text-lg max-w-2xl leading-relaxed"
            style={{ color: "rgba(255,255,255,0.75)", fontFamily: sans }}
          >
            We welcome every lawsuit. Each one is proof. Each one becomes public record.
            Each one costs them $50,000–$200,000. Each one costs us $0.
            This page documents them all — permanently.
          </p>
        </div>
      </header>

      <main className="mx-auto px-6 py-12 max-w-5xl space-y-16">

        {/* ── Stat counters ───────────────────────────────────────────────── */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 -mt-6">
          {stats.map((s) => (
            <Card
              key={s.label}
              className="p-6 text-center shadow-md"
              style={{ backgroundColor: "#fff", border: `1px solid ${border}`, borderRadius: "12px" }}
            >
              <div className="flex justify-center mb-3" style={{ color: s.color ?? navy }}>
                {s.icon}
              </div>
              <div
                className="text-4xl font-bold mb-1"
                style={{ color: s.color ?? ink, fontFamily: serif }}
              >
                {s.display}
              </div>
              <div
                className="text-xs font-semibold uppercase tracking-wider mb-1"
                style={{ color: muted }}
              >
                {s.label}
              </div>
              <div className="text-xs italic" style={{ color: muted }}>
                {s.note}
              </div>
            </Card>
          ))}
        </div>

        {/* ── Why we welcome section ───────────────────────────────────────── */}
        <section>
          <h2
            className="text-3xl font-bold mb-2"
            style={{ color: ink, fontFamily: serif }}
          >
            Why We Welcome Every Lawsuit
          </h2>
          <p className="mb-8 text-base" style={{ color: muted, fontFamily: sans }}>
            The strategic calculus is simple. Here's why a lawsuit against us is better for us than it is for them.
          </p>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {reasonCards.map((r) => (
              <Card
                key={r.title}
                className="p-6"
                style={{
                  backgroundColor: "#fff",
                  border: `1px solid ${border}`,
                  borderRadius: "12px",
                }}
              >
                <div
                  className="flex items-center gap-2 mb-3 font-semibold text-sm"
                  style={{ color: navy, fontFamily: sans }}
                >
                  <span style={{ color: red }}>{r.icon}</span>
                  {r.title}
                </div>
                <p
                  className="text-sm leading-relaxed"
                  style={{ color: muted, fontFamily: sans }}
                >
                  {r.body}
                </p>
              </Card>
            ))}
          </div>
        </section>

        {/* ── Case docket ─────────────────────────────────────────────────── */}
        <section>
          <h2
            className="text-3xl font-bold mb-2"
            style={{ color: ink, fontFamily: serif }}
          >
            Case Docket
          </h2>
          <p className="mb-6 text-sm" style={{ color: muted }}>
            Every filing, every dismissal, every outcome — documented publicly as they happen.
          </p>
          <Card
            className="shadow-sm overflow-hidden"
            style={{ backgroundColor: "#fff", border: `1px solid ${border}`, borderRadius: "12px" }}
          >
            <Table>
              <TableHeader>
                <TableRow style={{ borderColor: border, backgroundColor: "#FAF9F6" }}>
                  <TableHead style={{ color: navy, fontFamily: sans, fontWeight: 600 }}>Hospital / Health System</TableHead>
                  <TableHead style={{ color: navy, fontFamily: sans, fontWeight: 600 }}>Date Filed</TableHead>
                  <TableHead style={{ color: navy, fontFamily: sans, fontWeight: 600 }}>Court & Jurisdiction</TableHead>
                  <TableHead style={{ color: navy, fontFamily: sans, fontWeight: 600 }}>Alleged Violation</TableHead>
                  <TableHead className="text-right" style={{ color: navy, fontFamily: sans, fontWeight: 600 }}>Their Costs (Est.)</TableHead>
                  <TableHead className="text-right" style={{ color: navy, fontFamily: sans, fontWeight: 600 }}>Our Costs</TableHead>
                  <TableHead className="text-center" style={{ color: navy, fontFamily: sans, fontWeight: 600 }}>Outcome</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {LAWSUITS.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-20">
                      <div>
                        <Gavel
                          className="w-12 h-12 mx-auto mb-4 opacity-20"
                          style={{ color: navy }}
                        />
                        <div
                          className="text-2xl font-bold mb-2"
                          style={{ color: ink, fontFamily: serif }}
                        >
                          No lawsuits filed against us. Yet.
                        </div>
                        <div className="text-sm mb-1" style={{ color: muted }}>
                          Hospitals: our contact info is below.
                        </div>
                        <div className="text-xs mt-3" style={{ color: muted }}>
                          This page updates in real time when that changes.
                        </div>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : (
                  LAWSUITS.map((l) => (
                    <TableRow key={l.id} style={{ borderColor: `${border}40` }}>
                      <TableCell style={{ fontFamily: sans }}>
                        <div className="font-medium" style={{ color: ink }}>{l.hospitalName}</div>
                        {l.healthSystem && (
                          <div className="text-xs" style={{ color: muted }}>{l.healthSystem}</div>
                        )}
                      </TableCell>
                      <TableCell className="text-sm font-mono" style={{ color: muted }}>{l.dateFiled}</TableCell>
                      <TableCell className="text-sm" style={{ color: ink }}>
                        {l.court}
                        <div className="text-xs" style={{ color: muted }}>{l.jurisdiction}</div>
                      </TableCell>
                      <TableCell className="text-sm max-w-[200px]" style={{ color: ink }}>{l.allegedViolation}</TableCell>
                      <TableCell className="text-right font-mono text-sm" style={{ color: muted }}>
                        {l.theirEstLegalCosts ? fmt(l.theirEstLegalCosts) : "Unknown"}
                      </TableCell>
                      <TableCell className="text-right font-mono text-sm font-bold" style={{ color: green }}>
                        {fmt(l.ourLegalCosts)}
                      </TableCell>
                      <TableCell className="text-center">{outcomeBadge(l.outcome)}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </Card>
        </section>

        {/* ── Notice to hospitals ──────────────────────────────────────────── */}
        <section>
          <Card
            className="p-8"
            style={{
              backgroundColor: `${navy}08`,
              border: `1px solid ${navy}30`,
              borderRadius: "12px",
              borderLeft: `4px solid ${navy}`,
            }}
          >
            <div className="flex items-start gap-4">
              <div
                className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 mt-1"
                style={{ backgroundColor: `${navy}15` }}
              >
                <FileText className="w-5 h-5" style={{ color: navy }} />
              </div>
              <div className="flex-1">
                <h3
                  className="text-xl font-bold mb-4"
                  style={{ color: navy, fontFamily: serif }}
                >
                  If You're a Hospital Considering Suing Us
                </h3>
                <div className="space-y-3 text-sm leading-relaxed" style={{ color: ink, fontFamily: sans }}>
                  <p>
                    Everything published on this platform is factually accurate and derived
                    exclusively from your own legally-mandated public disclosures under
                    45 CFR Part 180.
                  </p>
                  <p>
                    The First Amendment protects public interest reporting on federal regulatory
                    compliance. Truth is an absolute defense to defamation claims. Anti-SLAPP
                    statutes in approximately 32 states exist specifically to protect organizations
                    like ours from strategic litigation — and in many states allow us to recover
                    costs if you file and lose.
                  </p>
                  <p>
                    Our operating entity holds no seizable assets. We represent ourselves in
                    litigation. Your legal fees begin at $50,000. Ours begin at $0. Every filing
                    becomes a public document, indexed and searchable, archived here permanently.
                  </p>
                  <p
                    className="font-semibold text-base pt-1"
                    style={{ color: green }}
                  >
                    There is a better option: update your chargemaster to comply with 45 CFR
                    Part 180. We will mark you compliant within 24 hours of verification — for
                    free. No lawyers required.
                  </p>
                </div>
                <div
                  className="mt-6 pt-5 border-t flex items-center gap-6 flex-wrap"
                  style={{ borderColor: `${navy}20` }}
                >
                  {/* Was href="mailto:contact@americanhospitalprices.com" until
                      2026-08-02. This is the single highest-value conversion on the
                      site — a hospital reading "update your chargemaster and we will
                      mark you compliant within 24 hours" and deciding to do it — and
                      it was gated behind a mailto that opens nothing for most people.
                      Routes to the contact form's compliance department instead, which
                      records the request in contact_submissions rather than depending
                      on an email arriving. */}
                  <Link
                    to="/contact?dept=hospital"
                    className="inline-flex items-center gap-2 text-sm font-semibold hover:opacity-80 transition-opacity"
                    style={{ color: navy, fontFamily: sans }}
                  >
                    <Mail className="w-4 h-4" />
                    Request compliance review
                  </Link>
                  <a
                    href="/data-sources"
                    className="inline-flex items-center gap-2 text-sm font-medium hover:opacity-80 transition-opacity"
                    style={{ color: green, fontFamily: sans }}
                  >
                    <ExternalLink className="w-4 h-4" />
                    View compliance status for all hospitals
                  </a>
                </div>
              </div>
            </div>
          </Card>
        </section>

        {/* ── Footer note ─────────────────────────────────────────────────── */}
        <p
          className="text-xs text-center pb-4"
          style={{ color: muted, fontFamily: sans }}
        >
          This platform is operated by{" "}
          <strong>Victims of Late Stage Capitalism</strong>. Each lawsuit filed
          against this platform is documented here as permanent public record under
          the First Amendment.
        </p>
      </main>
    </div>
  );
};

export default LawsuitsAgainstUs;
